import { Button } from '@/components/Button';
import { MdApartment, MdWork, MdStorage, MdLocationOn } from 'react-icons/md';
import type { IconType } from 'react-icons';
import { cn } from '@/lib/utils';
import Message from '@/components/Message';
import { OverflowTooltip } from '@/components/ui/overflow-tooltip';
import type {
  AgentApiProvidedJobPostsRecord,
  AgentApiSharedJobPostsItem,
} from '@/types/serializers';
import useBulkJobPosts from '../hooks/useBulkJobPosts';
import useSuggestSharedJobPosts from '../hooks/useSuggestSharedJobPosts';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';

type Props = {
  setStep: (step: 'add') => void;
  selectedJobPosts: AgentApiProvidedJobPostsRecord[];
  selectedSharedJobPosts: AgentApiSharedJobPostsItem[];
  candidateProfileId: number;
  close: () => void;
  onSuccess: () => void;
};

function ContentsCard({
  jobPost,
}: {
  jobPost: AgentApiProvidedJobPostsRecord | AgentApiSharedJobPostsItem;
}) {
  const companyName =
    'company' in jobPost && jobPost.company
      ? jobPost.company.name
      : 'company_name' in jobPost
        ? jobPost.company_name
        : null;

  // 自社求人は working_location、シェア求人は working_location_label で保持している
  const workingLocation =
    'working_location' in jobPost ? jobPost.working_location : jobPost.working_location_label;

  const infoItems = [
    companyName && { icon: MdApartment, text: companyName },
    jobPost.job_position_name && { icon: MdWork, text: jobPost.job_position_name },
    jobPost.salary_info && { icon: MdStorage, text: jobPost.salary_info },
    workingLocation && { icon: MdLocationOn, text: workingLocation },
  ].filter((item): item is { icon: IconType; text: string } => Boolean(item));

  return (
    <div className="flex flex-col gap-4.5 rounded border border-solid border-bright-gray p-lg shadow-layer1">
      <OverflowTooltip text={jobPost.title} className="text-lg font-bold text-[#333]" />
      {infoItems.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-md gap-y-sm">
          {infoItems.map(({ icon: Icon, text }, index) => (
            <div
              key={index}
              className={cn(
                'flex max-w-full min-w-0 items-center gap-xs',
                index < infoItems.length - 1 && 'border-r border-solid border-border pr-md'
              )}
            >
              <Icon className="size-5 shrink-0 text-main" />
              <OverflowTooltip text={text} className="text-xs text-main" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CandidatesJobPostEndpointsNewModalConfirmContents({
  setStep,
  selectedJobPosts,
  selectedSharedJobPosts,
  candidateProfileId,
  close,
  onSuccess,
}: Props) {
  const bulkJobPostsMutation = useBulkJobPosts(candidateProfileId);
  const suggestSharedJobPostsMutation = useSuggestSharedJobPosts(candidateProfileId);

  const handleSubmit = async () => {
    try {
      const providedJobPostIds = selectedJobPosts.map((jobPost) => jobPost.id);
      const sharedJobPostIds = selectedSharedJobPosts.map((jobPost) => jobPost.id);

      if (providedJobPostIds.length > 0) {
        await bulkJobPostsMutation.mutateAsync({
          provided_job_post_ids: providedJobPostIds,
        });
      }

      if (sharedJobPostIds.length > 0) {
        await suggestSharedJobPostsMutation.mutateAsync({
          shared_job_post_ids: sharedJobPostIds,
        });
      }

      showSuccessToast('提案求人を追加しました');
      onSuccess();
      close();
    } catch (error) {
      console.error('Error adding job posts:', error);
      showErrorToast('提案求人の追加に失敗しました');
    }
  };

  const totalCount = selectedJobPosts.length + selectedSharedJobPosts.length;

  return (
    <div className="flex h-[720px] max-h-[80vh] w-[1100px] max-w-[95vw] flex-col rounded bg-white">
      <div className="flex h-[56px] shrink-0 items-center border-b border-solid border-border px-lg">
        <p className="text-base font-bold">提案求人の追加</p>
      </div>
      <div className="hidden-scrollbar min-h-0 flex-1 overflow-y-auto p-lg">
        {selectedSharedJobPosts.length > 0 && (
          <Message color="caution" className="mb-md">
            <p className="text-sm">データベース求人を提案すると自社求人へ複製されます。</p>
          </Message>
        )}
        <p className="text-base leading-[1.7] font-normal text-black">
          以下の求人を提案してもよろしいですか？
        </p>
        <div className="mt-md flex flex-col gap-md">
          {selectedJobPosts.map((jobPost) => (
            <ContentsCard key={`provided-${jobPost.id}`} jobPost={jobPost} />
          ))}
          {selectedSharedJobPosts.map((jobPost) => (
            <ContentsCard key={`shared-${jobPost.id}`} jobPost={jobPost} />
          ))}
        </div>
      </div>
      <div className="flex h-[74px] shrink-0 items-center justify-between border-t border-solid border-border px-lg">
        <div className="flex items-end gap-[3px] text-main">
          <p className="text-xl leading-[1.4] font-bold">{totalCount}件</p>
          <p className="text-xs">選択中</p>
        </div>
        <div className="flex items-center gap-md">
          <Button color="secondary" onClick={() => setStep('add')}>
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={bulkJobPostsMutation.isPending || suggestSharedJobPostsMutation.isPending}
          >
            {bulkJobPostsMutation.isPending || suggestSharedJobPostsMutation.isPending
              ? '送信中...'
              : '提案求人を追加'}
          </Button>
        </div>
      </div>
    </div>
  );
}
